import type { Block, BlockPaletteEntry, BlockType, PageContent } from './pages-api';

/**
 * Editing a block tree, as pure functions over a path.
 *
 * A path is the indexes to walk: `[2]` is the third block, `[2, 0]` the first block inside it.
 * Only `COLUMNS` nests, and only one level, so a path is never longer than two — but writing it
 * generally costs nothing and means the rule lives in one place (`canNest`) rather than in every
 * call site.
 *
 * Pure, so the screen never mutates what it is rendering and so this is testable without a
 * browser. A builder that drops a column when somebody deletes the block above it is a bug that
 * only shows up on a page a customer is already looking at.
 */

export type Path = number[];

/** The smallest valid block of each type — what "add this block" should produce. */
export function emptyBlock(type: BlockType): Block {
  const props: Record<string, unknown> = {
    HEADING: { text: '', level: 2 },
    TEXT: { text: '' },
    IMAGE: { src: '' },
    BUTTON: { label: '', href: '' },
    FORM: { formId: '' },
    VIDEO: { src: '' },
    SPACER: { height: 32 },
    DIVIDER: {},
    TESTIMONIAL: { quote: '' },
    PRICING: { name: '', amountCents: 0 },
    FAQ: { items: [{ question: '', answer: '' }] },
    COLUMNS: {},
  }[type];

  return type === 'COLUMNS' ? { type, props, children: [] } : { type, props };
}

function at(blocks: Block[], path: Path): Block | undefined {
  let current: Block | undefined;
  let list = blocks;

  for (const index of path) {
    current = list[index];
    if (!current) return undefined;
    list = current.children ?? [];
  }

  return current;
}

/** Rebuilds the tree with one list replaced; everything outside that list is the same object. */
function rewrite(blocks: Block[], path: Path, change: (list: Block[]) => Block[]): Block[] {
  if (path.length <= 1) return change(blocks);

  const index = path[0]!;

  return blocks.map((block, position) =>
    position === index
      ? { ...block, children: rewrite(block.children ?? [], path.slice(1), change) }
      : block,
  );
}

export function addBlock(content: PageContent, into: Path, type: BlockType): PageContent {
  return {
    ...content,
    blocks: rewrite(content.blocks, [...into, 0], (list) => [...list, emptyBlock(type)]),
  };
}

export function updateBlock(
  content: PageContent,
  path: Path,
  change: (block: Block) => Block,
): PageContent {
  if (!at(content.blocks, path)) return content;

  return {
    ...content,
    blocks: rewrite(content.blocks, path, (list) =>
      list.map((block, index) => (index === path.at(-1) ? change(block) : block)),
    ),
  };
}

export function setProp(
  content: PageContent,
  path: Path,
  key: string,
  value: unknown,
): PageContent {
  return updateBlock(content, path, (block) => {
    const props = { ...block.props };

    // An empty optional prop is removed rather than stored: the server strips unknown props but
    // keeps empty ones, and a page carrying `align: ''` renders nothing and reads as a bug.
    if (value === undefined || value === '') delete props[key];
    else props[key] = value;

    return { ...block, props };
  });
}

export function removeBlock(content: PageContent, path: Path): PageContent {
  if (!at(content.blocks, path)) return content;

  return {
    ...content,
    blocks: rewrite(content.blocks, path, (list) =>
      list.filter((_, index) => index !== path.at(-1)),
    ),
  };
}

export function moveBlock(content: PageContent, path: Path, direction: -1 | 1): PageContent {
  if (!at(content.blocks, path)) return content;

  const from = path.at(-1)!;
  const to = from + direction;

  return {
    ...content,
    blocks: rewrite(content.blocks, path, (list) => {
      if (to < 0 || to >= list.length) return list;

      const next = [...list];
      const [moved] = next.splice(from, 1);

      next.splice(to, 0, moved!);

      return next;
    }),
  };
}

export function countBlocks(blocks: Block[]): number {
  return blocks.reduce((total, block) => total + 1 + countBlocks(block.children ?? []), 0);
}

/**
 * What is missing, before the server is asked.
 *
 * The server validates and is the authority; this exists so an empty required prop reads as a
 * sentence beside the field rather than as a 422 after pressing save.
 */
export interface Problem {
  path: Path;
  type: BlockType;
  key: string;
}

export function problemsIn(
  blocks: Block[],
  palette: BlockPaletteEntry[],
  path: Path = [],
): Problem[] {
  const problems: Problem[] = [];

  for (const [index, block] of blocks.entries()) {
    const here = [...path, index];
    const entry = palette.find((candidate) => candidate.type === block.type);

    for (const prop of entry?.props ?? []) {
      if (!prop.required) continue;

      const value = block.props[prop.key];
      const empty =
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value.length === 0);

      if (empty) problems.push({ path: here, type: block.type, key: prop.key });
    }

    problems.push(...problemsIn(block.children ?? [], palette, here));
  }

  return problems;
}

/** Whether this block may hold others. Read from the palette, so the server decides. */
export function canNest(palette: BlockPaletteEntry[], type: BlockType): boolean {
  return palette.find((entry) => entry.type === type)?.nests === true;
}
